/**
 * Calendar Primitive (Story 5.8)
 *
 * Wraps react-day-picker v9 with project styling (Tailwind v4).
 * Used inside Popover for date range selection in reports.
 *
 * Usage:
 *   import { Calendar } from "@provenleap/ui";
 *   <Calendar mode="range" selected={range} onSelect={setRange} numberOfMonths={2} />
 */

import { DayPicker } from "react-day-picker";
import type { DayPickerProps } from "react-day-picker";
import { CaretLeft, CaretRight, CaretUp, CaretDown } from "@phosphor-icons/react";

import { cn } from "../lib/utils";

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: DayPickerProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row gap-2",
        month: "flex flex-col gap-4",
        month_caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium",
        nav: "flex items-center gap-1",
        button_previous:
          "absolute left-1 top-0 inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors motion-reduce:transition-none hover:bg-accent hover:text-accent-foreground h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 z-10",
        button_next:
          "absolute right-1 top-0 inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors motion-reduce:transition-none hover:bg-accent hover:text-accent-foreground h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 z-10",
        month_grid: "w-full border-collapse space-y-1",
        weekdays: "flex",
        weekday:
          "text-muted-foreground rounded-md w-11 font-normal text-[0.8rem]",
        week: "flex w-full mt-2",
        day: "h-11 w-11 text-center text-sm p-0 relative focus-within:relative focus-within:z-20",
        day_button: cn(
          "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm ring-offset-background",
          "transition-colors motion-reduce:transition-none",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2",
          "hover:bg-accent hover:text-accent-foreground",
          "h-11 w-11 p-0 font-normal aria-selected:opacity-100"
        ),
        range_end: "day-range-end",
        selected:
          "bg-brand text-white hover:bg-brand hover:text-white focus:bg-brand focus:text-white",
        today: "bg-accent text-accent-foreground",
        outside:
          "day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
        disabled: "text-muted-foreground opacity-50",
        range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) => {
          switch (orientation) {
            case "left":
              return <CaretLeft className="h-4 w-4" />;
            case "up":
              return <CaretUp className="h-4 w-4" />;
            case "down":
              return <CaretDown className="h-4 w-4" />;
            default:
              return <CaretRight className="h-4 w-4" />;
          }
        },
      }}
      {...props}
    />
  );
}

Calendar.displayName = "Calendar";

export { Calendar };
export type { DayPickerProps as CalendarProps };
